import { useState, useContext } from "react";
import styles from "./notification.module.scss";
import { Badge, IconButton, Menu, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppContext, AppAction } from "helpers/context";
import { updateUserNotifications } from "api/layer";

export default function HeaderNotification() {
  const [t] = useTranslation();
  const [context, dispatch] = useContext(AppContext);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const userNotifications = context.userNotifications as {
    bannerNotifications?: any[];
    ringNotifications?: any[];
  };
  const ringNotifications = userNotifications?.ringNotifications || [];
  const unreadCount = ringNotifications.filter(
    (item: any) => !item.isRead
  ).length;

  const getTypeFromHeaderType = (headerType: string) => {
    const lowerType = headerType?.toLowerCase() || "";
    if (lowerType.includes("release")) return "release";
    if (lowerType.includes("system")) return "system";
    if (lowerType.includes("welcome")) return "welcome";
    return "system";
  };

  const formatDate = (dateTime: string) => {
    if (!dateTime) return "";
    const date = new Date(dateTime);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = () => {
    const unreadNotifications = ringNotifications.filter(
      (item: any) => !item.isRead
    );
    if (unreadNotifications.length > 0) {
      const params = unreadNotifications.map((item: any) => ({
        notificationId: item.notificationId,
        notificationCategory: item.notificationCategory,
        isRead: true,
      }));
      updateUserNotifications(params).then(() => {
        // Update context to mark all as read
        const updatedRingNotifications = ringNotifications.map((item: any) => ({
          ...item,
          isRead: true,
        }));
        dispatch({
          type: AppAction.setUserNotifications,
          payload: {
            ...userNotifications,
            ringNotifications: updatedRingNotifications,
          },
        });
      });
    }
  };

  const handleMessageClick = (item: any) => {
    // Mark as read
    if (item.notificationId && !item.isRead) {
      updateUserNotifications([
        {
          notificationId: item.notificationId,
          notificationCategory: item.notificationCategory,
          isRead: true,
        },
      ]).then(() => {
        // Update context to mark item as read
        const updatedRingNotifications = ringNotifications.map((n: any) =>
          n.notificationId === item.notificationId ? { ...n, isRead: true } : n
        );
        dispatch({
          type: AppAction.setUserNotifications,
          payload: {
            ...userNotifications,
            ringNotifications: updatedRingNotifications,
          },
        });
      });
    }

    // Open release page if it's a release type
    const type = getTypeFromHeaderType(
      item.notificationHeader?.notificationHeaderType
    );
    if (type === "release" && item.notificationMessage?.id) {
      window.open(
        `${window.origin}/resources?id=${item.notificationMessage.id}`,
        "_blank"
      );
    }
  };

  const getTitleByType = (type: string) => {
    switch (type) {
      case "release":
        return t("userSystem.notificationTypeRelease");
      case "system":
        return t("userSystem.notificationTypeSystemNotification");
      case "welcome":
        return t("userSystem.notificationTypeWelcome");
      default:
        return type;
    }
  };
  const getIconNameByType = (type: string) => {
    switch (type) {
      case "release":
        return (
          <span className="material-icons-outlined text-[24px] mt-[2px]">
            new_releases
          </span>
        );
      case "system":
        return (
          <span className="material-icons-outlined text-[24px] mt-[2px]">
            settings
          </span>
        );
      case "welcome":
        return (
          <span className="material-icons-outlined text-[24px] mt-[2px]">
            waving_hand
          </span>
        );
      default:
        return (
          <span className="material-icons-outlined text-[24px] mt-[2px]">
            notifications
          </span>
        );
    }
  };

  return (
    <div className={styles.wrapper}>
      <Tooltip
        title={t("header.notification.title")}
        placement="bottom"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -12],
                },
              },
            ],
          },
        }}
      >
        <IconButton
          id="basic-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          className={styles.customIconButton}
          sx={{
            padding: "12px"
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                height: "16px",
                minWidth: "16px",
                padding: "0 4px",
              },
            }}
          >
            <div className="material-icons">notifications</div>
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            "& .MuiList-root": {
              py: 0,
            },
          },
        }}
      >
        <ul className="card compact dropdown-content z-[1] p-0 shadow bg-base-100  w-[315px] rounded-[4px] overflow-hidden">
          <li>
            <div className="flex justify-between items-center rounded-none text-[14px] px-4 py-0 h-12">
              <span className="text-[#0009] font-medium">
                {t("header.notification.title")}
              </span>
              <span
                className="text-[#007934] text-[13px] cursor-pointer"
                onClick={handleMarkAllRead}
              >
                {t("header.notification.markAllRead")}
              </span>
            </div>
          </li>
          <ul
            style={{
              maxHeight: "calc(100vh - 320px)",
              overflow: "auto",
            }}
          >
            {ringNotifications.length === 0 ? (
              <li>
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <span className="material-icons text-[48px] text-[#ccc] mb-2">
                    notifications_none
                  </span>
                  <span className="text-[#999] text-[14px]">
                    {t("header.notification.noNotifications")}
                  </span>
                </div>
              </li>
            ) : (
              ringNotifications.slice(0, 30).map((item: any, index: number) => {
                const type = getTypeFromHeaderType(
                  item.notificationHeader?.notificationHeaderType
                );
                return (
                  <li key={item.notificationId || index}>
                    <div className="border-t-[1px] border-solid border-[#0000001f]"></div>
                    <div
                      className={styles.messageItemWrapper}
                      style={{
                        backgroundColor: item.isRead
                          ? "#fff"
                          : "rgba(0, 0, 0, 0.04)",
                        borderLeft: item.isRead ? "none" : "4px solid #007934",
                        cursor: type === "release" ? "pointer" : "default",
                      }}
                      onClick={() => handleMessageClick(item)}
                    >
                      <div className={styles.messageItemIconWrapper}>
                        {getIconNameByType(type)}
                      </div>
                      <div className={styles.messageItemInfoWrapper}>
                        <span className="text-[#7D7D7D] text-[12px] leading-[16px]">
                          {getTitleByType(type)}
                        </span>
                        <span className="text-[#3f3f3f] text-[13px] leading-[16px]">
                          {item.notificationHeader?.notificationHeaderMessage}
                        </span>
                        <div className="flex gap-1 justify-between">
                          <span className="text-[#7D7D7D] text-[12px] leading-[16px]">
                            {item.notificationFooter}
                          </span>
                          <span className="text-[#7D7D7D] text-[12px] leading-[16px]">
                            {formatDate(item.notificationCreatedDateTime)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </ul>
      </Menu>
    </div>
  );
}
